import styled from '@emotion/styled';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { evaluateKpi, formatKpiValue, KpiMetricType } from '../../../helpers/kpi';
import { theme } from '../../../theme';
import { Badge } from '../../badge/Badge';
import { KpiBar } from '../../charts/KpiBar';
import { BodyMedium, HeadingMedium } from '../../Typography';
import { ChevronRightIcon } from '../../../assets/icons';
import { badgeStateToColor, getScoreColor, KpiCardIconButton } from '../kpiCardShared';

export type KpiCardProps = {
  title: string;
  subtext?: string;
  /** Raw metric value. */
  value: number;
  /** Metric type — selects the bar variant, the score format and (for scale) the badge. */
  metricType: KpiMetricType;
  /** Lower bound of the value range. Only used for `metricType: 'custom'` (default 0) — other metric types have a fixed range. */
  min?: number;
  /** Upper bound of the value range. Only used for `metricType: 'custom'` (default 100). */
  max?: number;
  /** Value from which the bar/score is shown as `success`. Required for `metricType: 'custom'`. */
  successFrom?: number;
  /** Value from which the bar/score is shown as `warning` (below `successFrom`). Required for `metricType: 'custom'`. */
  warningFrom?: number;
  /** Ready-to-render score text; replaces the default formatting of `value`. */
  formattedValue?: string;
  /** Hide the interpretation badge, e.g. for `custom` metrics without thresholds. */
  hideBadge?: boolean;
  /** Called when the secondary icon button (navigate to detail) is clicked. */
  onDetailClick?: () => void;
  /** Accessible label for the detail button. */
  detailButtonLabel?: string;
  className?: string;
  'data-cy'?: string;
  'data-icom'?: string;
};

export const KpiCard: FC<KpiCardProps> = ({
  title,
  subtext,
  value,
  metricType,
  min,
  max,
  successFrom,
  warningFrom,
  formattedValue,
  hideBadge = false,
  onDetailClick,
  detailButtonLabel,
  className,
  'data-cy': dataCy = 'kpi-card',
  'data-icom': dataIcom,
}) => {
  const { t } = useTranslation('kpiCard');
  const {
    state,
    badgeText,
    min: rangeMin,
    max: rangeMax,
  } = evaluateKpi(value, metricType, t, {
    min,
    max,
    customThresholds: successFrom !== undefined && warningFrom !== undefined ? { successFrom, warningFrom } : undefined,
  });
  const isNps = metricType === 'nps';
  const valueColor = getScoreColor(state);
  return (
    <CardWrapper className={className} data-cy={dataCy} data-icom={dataIcom}>
      <div>
        <Header>
          <HeadingMedium margin="0" bold data-cy={`${dataCy}-title`}>
            {title}
          </HeadingMedium>
          {onDetailClick && (
            <KpiCardIconButton
              type="button"
              onClick={onDetailClick}
              aria-label={detailButtonLabel || t('detailButtonLabel')}
              data-cy={`${dataCy}-detail-button`}
              data-icom="kpi-card-detail-button"
            >
              <ChevronRightIcon />
            </KpiCardIconButton>
          )}
        </Header>
        {subtext && (
          <BodyMedium margin="0" color={theme.colors.text.secondary} data-cy={`${dataCy}-subtext`}>
            {subtext}
          </BodyMedium>
        )}
      </div>
      <BarWrapper>
        <KpiBar
          value={value}
          min={rangeMin}
          max={rangeMax}
          variant={isNps ? 'nps' : 'scale'}
          state={state}
          data-cy={`${dataCy}-bar`}
        />
      </BarWrapper>
      <Footer>
        <HeadingMedium margin="0" bold data-cy={`${dataCy}-score`} color={valueColor}>
          {formattedValue ?? formatKpiValue(value, metricType)}
        </HeadingMedium>
        {!hideBadge && (
          <Badge text={badgeText} color={badgeStateToColor(state)} subtle size="Small" data-cy={`${dataCy}-badge`} />
        )}
      </Footer>
    </CardWrapper>
  );
};

const CardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.e};
  padding: ${theme.spacing.g};
  background-color: ${theme.colors.backgroundCover.default};
  border-radius: ${theme.borderRadius.l};
  width: 100%;
  margin-bottom: ${theme.spacing.f};
  margin-right: ${theme.spacing.f};
  border: 1px solid #ededed;
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.f};
`;

const BarWrapper = styled.div`
  width: 100%;
`;

const Footer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${theme.spacing.e};
`;
